/**
 * Theme Management — API client.
 *
 * A thin, centralized wrapper over the shared axios `API` instance
 * (exported from App.js). Every call automatically attaches the JWT
 * from cookies (matching the rest of the admin panel) and targets the
 * new `/theme-management` backend routes ONLY — it never touches the
 * legacy theme endpoints.
 *
 * Grouping every endpoint here keeps pages declarative and makes the
 * request surface easy to audit / change in one place.
 */
import Cookies from "js-cookie";
import { API } from "../App";

/** Build the auth config; multipart is inferred by axios from FormData. */
const auth = () => ({
  headers: { authorization: `Bearer ${Cookies.get("jwt")}` },
});

// The Theme Management module is mounted under `/api` on the backend
// (app.use("/api", ThemeManagementRouter)). The shared API base URL has
// no `/api`, so prefix it here for every module request.
const MOUNT = "/api";
const post = (url, payload) => API.post(`${MOUNT}${url}`, payload, auth());
/** Like `post`, but merges extra axios config (e.g. onUploadProgress). */
const postWithConfig = (url, payload, config = {}) =>
  API.post(`${MOUNT}${url}`, payload, { ...auth(), ...config });

/* ------------------------------ Themes ----------------------------- */
export const themesApi = {
  list: (params) => post("/theme-management/themes/list", params),
  options: () => post("/theme-management/themes/options", {}),
  categories: () => post("/theme-management/themes/categories", {}),
  detail: (id) => post(`/theme-management/themes/detail/${id}`, {}),
  create: (formData) => post("/theme-management/themes", formData),
  update: (id, formData) => post(`/theme-management/themes/update/${id}`, formData),
  changeStatus: (id, status) => post(`/theme-management/themes/status/${id}`, { status }),
  changePremium: (id, premium) => post(`/theme-management/themes/premium/${id}`, { premium }),
  remove: (id) => post(`/theme-management/themes/delete/${id}`, {}),
  updateSort: (items) => post("/theme-management/themes/sort", { items }),
};

/* --------------------- Theme Icons (Icon Sets) --------------------- */
export const iconsApi = {
  list: (params) => post("/theme-management/icons/list", params),
  detail: (id) => post(`/theme-management/icons/detail/${id}`, {}),
  create: (formData) => post("/theme-management/icons", formData),
  update: (id, formData) => post(`/theme-management/icons/update/${id}`, formData),
  changeStatus: (id, status) => post(`/theme-management/icons/status/${id}`, { status }),
  changePremium: (id, premium) => post(`/theme-management/icons/premium/${id}`, { premium }),
  remove: (id) => post(`/theme-management/icons/delete/${id}`, {}),
  bulkDelete: (ids) => post("/theme-management/icons/bulk-delete", { ids }),
  updateSort: (items) => post("/theme-management/icons/sort", { items }),
  /** Upload a ZIP of PNG icons for review. `onUploadProgress` is optional. */
  bulkImport: (formData, onUploadProgress) =>
    postWithConfig("/theme-management/icons/bulk-import", formData, {
      onUploadProgress,
    }),
};

/* ----------- Default Icons (read-only — seed data, prefill) -------- */
export const defaultIconsApi = {
  list: () => post("/theme-management/default-icons/list", {}),
};

/* ----------- Widget subcategories (main router, no /api prefix) ---- */
export const widgetCategoriesApi = {
  list: () => API.post("/widget_category", {}, auth()),
};

/* -------------------------- Theme Widgets -------------------------- */
export const widgetsApi = {
  list: (params) => post("/theme-management/widgets/list", params),
  detail: (id) => post(`/theme-management/widgets/detail/${id}`, {}),
  create: (payload) => post("/theme-management/widgets", payload),
  update: (id, payload) => post(`/theme-management/widgets/update/${id}`, payload),
  changeStatus: (id, status) => post(`/theme-management/widgets/status/${id}`, { status }),
  remove: (id) => post(`/theme-management/widgets/delete/${id}`, {}),
  updateSort: (items) => post("/theme-management/widgets/sort", { items }),
};

/* -------------------------- Widget Variants ------------------------ */
export const variantsApi = {
  create: (formData) => post("/theme-management/variants", formData),
  update: (id, formData) => post(`/theme-management/variants/update/${id}`, formData),
  changeStatus: (id, status) => post(`/theme-management/variants/status/${id}`, { status }),
  updateSort: (items) => post("/theme-management/variants/sort", { items }),
  remove: (id) => post(`/theme-management/variants/delete/${id}`, {}),
};

/**
 * Small helper: is this response an auth failure? The backend returns
 * response_code 0/401/403 for auth problems — pages use this to bounce
 * the user back to the login screen.
 */
export const isAuthError = (res) =>
  res && res.data && [0, 401, 403].includes(res.data.response_code);

/**
 * Extract the most useful human-readable error message from an API
 * response body. Field-level validation errors (e.g. `{ text_styles:
 * "Opacity for role \"time\" must be between 0 and 1" }`) are far more
 * actionable than the generic top-level `response_message` ("Validation
 * failed"), so prefer the first one when present.
 */
export const apiErrorMessage = (data, fallback = "Request failed") => {
  if (!data) return fallback;
  if (data.errors && typeof data.errors === "object") {
    const first = Object.values(data.errors).find(Boolean);
    if (first) return first;
  }
  return data.response_message || fallback;
};
